import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faArchive,
  faBars,
  faCalendarDay,
  faEdit,
  faKeyboard,
  faSearch,
  faShareFromSquare,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import {FormProvider, useForm} from "react-hook-form";
import {useNavigate, useParams} from "react-router-dom";

// Contexts
import {SettingsContext} from "../../contexts/settings.js";
import {AuthContext} from "../../contexts/auth.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";
import usePrevious from "../../hooks/usePrevious.js";

// Utils
import {prettyDateInUserTimezone} from "../../utils/helpers.js";

// Components
import BaseTable from "../../components/BaseTable.js";
import Dropdown from "../../components/Dropdown.js";
import ToggleSwitch from "../../components/ToggleSwitch.js";

// Styles
import {bp} from "../../style/components/breakpoints.js";
import {border, pad, radius, shadow} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {
  Inline,
  Heading,
  TableWrapper,
  TableFooter,
  Search,
  SearchWrapper,
  SearchIcon,
  Button,
  Arrow,
  Abbr,
  ButtonWrapper,
  Small
} from "../../style/components/general.js";
import {flex, z} from "../../style/components/mixins.js";

const TableReportSharepoint = ({
  index,
  report,
  showDelete,
  archiveReport,
  restoreReport,
  publishReport,
  onOpen,
  triggerOpen,
  handledOpen
}) => {
  const isMounted = useMountedState();

  const {slug} = useParams();
  const navigate = useNavigate();

  const {settings} = useContext(SettingsContext);
  const {currentUser, atLeast, roleCanAccessResource} = useContext(AuthContext);

  const {timezone} = settings;

  // Custom Table
  const [open, setOpen] = useState(false);
  const [tableData, setTableData] = useState();
  // Base Table
  const [query, setQuery] = useState();
  const [orderBy, setOrderBy] = useState("desc");
  const [groupBy, setGroupBy] = useState("name");
  const [limit, setLimit] = useState(10);
  const [loadMore, setLoadMore] = useState();
  const [searchType, setSearchType] = useState("text");

  // Modals
  const [showFilters, setShowFilters] = useState(false);

  const filterMenu = useRef(null);

  useEffect(() => {
    const handleClickOutsideFilterMenu = e =>
      filterMenu &&
      filterMenu.current !== null &&
      !filterMenu.current.contains(e.target) &&
      !filterMenu.current.contains(e.target.nextSibling) &&
      setShowFilters(false);

    if (filterMenu) document.addEventListener("mousedown", handleClickOutsideFilterMenu);

    // Cleanup
    return () => document.removeEventListener("mousedown", handleClickOutsideFilterMenu);
  }, [filterMenu]);

  // Previous Hooks
  const prevOrderBy = usePrevious(orderBy);
  const prevGroupBy = usePrevious(groupBy);
  const prevLimit = usePrevious(limit);

  const {api} = useApi("report-records");
  const {api: apiMS, loading} = useApi("microsoft", {suppress: {longRequest: false}});

  const form = useForm({
    defaultValues: {
      search: ""
    }
  });

  const {reset, register} = form;

  const formatTableData = useCallback(
    payload =>
      payload.map(item => ({
        preview: (
          <Link href={item.webUrl} target="_blank">
            View
          </Link>
        ),
        name: item.name,
        createdAt: prettyDateInUserTimezone(item.createdDateTime, timezone),
        recordId: item.id
      })),
    [timezone]
  );

  const getTableData = useCallback(() => {
    const filter = {};

    if (query) filter.Search = query;

    const payload = {
      resourceType: "sharepoint",
      resourcePath: "drive",
      resourceId: report.sharepointFolderId,
      orderBy: orderBy,
      groupBy,
      limit,
      filter: JSON.stringify(filter)
    };

    apiMS.callGet(null, payload).then(({status, data}) => {
      if (status === 200 && data?.drive) {
        const formatted = formatTableData(data.drive.filter(({folder}) => !folder));
        setTableData(formatted);
        setLoadMore(data?.next);
      } else if (!data?.drive) {
        setTableData([]);
        setLoadMore(null);
      }
    });
  }, [apiMS, groupBy, limit, orderBy, query, formatTableData, report]);

  // Open first table
  useEffect(() => {
    if (isMounted() && index === 1 && !handledOpen?.current) {
      if (handledOpen) handledOpen.current = true;
      setOpen(true);
      if (onOpen) onOpen(true, report.id);
    } else if (isMounted() && onOpen) onOpen(open, report.id);
  }, [isMounted, onOpen, index, report.id, open, handledOpen]);

  useEffect(() => {
    if (isMounted() && triggerOpen !== undefined && triggerOpen !== open) setOpen(triggerOpen);
  }, [isMounted, onOpen, triggerOpen, report.id, open]);

  // Initial Load
  useEffect(() => {
    if (isMounted() && open && !tableData) getTableData();
  }, [isMounted, open, tableData, getTableData]);

  // Update rows on sort or limit
  useEffect(() => {
    if (
      isMounted() &&
      prevOrderBy &&
      prevGroupBy &&
      prevLimit &&
      (prevOrderBy !== orderBy || prevGroupBy !== groupBy || prevLimit !== limit)
    )
      getTableData();
  }, [isMounted, getTableData, prevOrderBy, prevGroupBy, prevLimit, orderBy, groupBy, limit]);

  // Search
  useEffect(() => {
    if (open && (query || query === "")) getTableData();
  }, [open, query, getTableData]);

  const handleSearch = e => setQuery(e.target.value);

  const generateReport = () =>
    api.callPost({reportId: report.id, userId: currentUser.publicId}).then(({status}) => {
      if (status === 201) {
        setShowFilters(false);
        getTableData();
      }
    });

  return (
    <CustomTableWrapper key={report.name}>
      <TableMenuRow>
        <Inline>
          <ReportName>
            <abbr title={report.name}>{report.name}</abbr>
          </ReportName>
          {report?.frequency?.name && (
            <Frequency>
              <abbr title={report.frequency.name.toUpperCase()}>
                {report.frequency.name.toUpperCase()}
              </abbr>
            </Frequency>
          )}
          {report?.draft && <Archived>DRAFT</Archived>}
          {report.isArchived && <Archived>ARCHIVED</Archived>}
        </Inline>
        <Inline>
          <ButtonWrapper
            type="button"
            onClick={() => {
              setOpen(prev => {
                if (onOpen) onOpen(!prev, report.id);
                return !prev;
              });
              setTableData(null);
            }}>
            <Arrow rotate={!open ? "180deg" : "270deg"} />
          </ButtonWrapper>
        </Inline>
      </TableMenuRow>
      {open && (
        <div>
          <TableMenuRow>
            <StyledSearchWrapper>
              {searchType === "text" && (
                <SearchIcon>
                  <FontAwesomeIcon icon={faSearch} />
                </SearchIcon>
              )}
              <FormProvider {...form}>
                <Search
                  key={searchType}
                  name="search"
                  type={searchType}
                  placeholder="Search..."
                  {...register("search")}
                  onChange={e => handleSearch(e)}
                />
              </FormProvider>
              <Spaced>
                <ToggleSwitch
                  iconLeft={faKeyboard}
                  iconRight={faCalendarDay}
                  textLeft="Text"
                  textRight="Date"
                  onChange={() => {
                    setSearchType(prev => (prev === "text" ? "date" : "text"));
                    reset({search: ""});
                  }}
                  options={["text", "date"]}
                  defaultSelected="text"
                />
              </Spaced>
            </StyledSearchWrapper>
            <Inline>
              {atLeast("creator") && (
                <>
                  {roleCanAccessResource("report", "update") &&
                    !report.isArchived &&
                    report.draft && (
                      <Button type="Button" onClick={() => publishReport(report)}>
                        <Abbr title="Publish">
                          <FontAwesomeIcon icon={faShareFromSquare} />
                        </Abbr>
                      </Button>
                    )}
                  {roleCanAccessResource("report", "update") && !report.isArchived && (
                    <Button
                      type="Button"
                      onClick={() => navigate(`/facilities/${slug}/reports/${report.id}/edit`)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                  )}
                  {roleCanAccessResource("report", "archive") && (
                    <Archive
                      restore={report.isArchived}
                      type="button"
                      onClick={() =>
                        !report.isArchived ? archiveReport(report) : restoreReport(report)
                      }>
                      {!report.isArchived ? (
                        <Abbr title="Archive">
                          <FontAwesomeIcon icon={faArchive} />
                        </Abbr>
                      ) : (
                        "Restore"
                      )}
                    </Archive>
                  )}
                  {roleCanAccessResource("report", "delete") && (
                    <Delete type="button" onClick={() => showDelete(report)}>
                      <Abbr title="Delete">
                        <FontAwesomeIcon icon={faTrash} />
                      </Abbr>
                    </Delete>
                  )}
                  {roleCanAccessResource("report_record", "create") && (
                    <PositionedDiv>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowFilters(!showFilters);
                        }}>
                        <FontAwesomeIcon icon={faBars} />
                      </Button>
                      {showFilters && (
                        <OptionSub ref={filterMenu}>
                          <ApplyFilter type="button" onClick={generateReport}>
                            Generate
                          </ApplyFilter>
                        </OptionSub>
                      )}
                    </PositionedDiv>
                  )}
                </>
              )}
            </Inline>
          </TableMenuRow>
          <RowWrapper>
            <BaseTable
              headings={{
                preview: {header: "View", disabled: true},
                name: {header: "Name", disabled: false},
                createdAt: {header: "Created At", disabled: true}
              }}
              data={tableData}
              loading={loading}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              groupBy={groupBy}
              setGroupBy={setGroupBy}
            />
          </RowWrapper>
          <TableFooter>
            <PageLimit>
              <Dropdown
                options={[10, 20]}
                selection={limit}
                setSelection={selection => setLimit(selection)}
              />
            </PageLimit>
            {loadMore && (
              <Button
                type="button"
                onClick={() =>
                  apiMS
                    .callGet(null, {
                      resourceType: "sharepoint",
                      resourcePath: "drive",
                      next: loadMore
                    })
                    .then(({status, data}) => {
                      if (status === 200 && data?.drive) {
                        setTableData(prev => [...prev, ...formatTableData(data.drive)]);
                        setLoadMore(data?.next);
                      }
                    })
                }>
                Load More
              </Button>
            )}
          </TableFooter>
        </div>
      )}
    </CustomTableWrapper>
  );
};

TableReportSharepoint.propTypes = {
  index: PropTypes.number.isRequired,
  report: PropTypes.objectOf(PropTypes.any).isRequired,
  showDelete: PropTypes.func.isRequired,
  archiveReport: PropTypes.func.isRequired,
  restoreReport: PropTypes.func.isRequired,
  publishReport: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  triggerOpen: PropTypes.bool,
  handledOpen: PropTypes.objectOf(PropTypes.any)
};

// Style Overrides
const CustomTableWrapper = styled(TableWrapper)`
  min-width: 350px;
`;

const TableMenuRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${pad}px;
`;

const PageLimit = styled.div`
  margin-right: ${pad}px;

  select {
    padding: ${pad / 2}px;
  }
`;

const RowWrapper = styled.div`
  position: relative;
`;

const Link = styled.a`
  ${voice.normal};
  padding: ${pad / 2}px ${pad}px;
  border-radius: ${radius};
  max-width: max-content;
  background-color: ${({theme}) => theme.secondary};
  color: ${({theme}) => theme.tertiary};
`;

const ReportName = styled(Heading)`
  text-overflow: ellipsis;
  overflow-x: hidden;
  display: block;
  white-space: nowrap;
  max-width: 65%;

  ${bp(2)} {
    max-width: inherit;
  }
`;

const Frequency = styled(Small)`
  width: fit-content;
  padding: ${pad / 4}px ${pad / 2}px;
  border-radius: 10px;
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.primary};
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  white-space: nowrap;
  ${voice.quiet};
`;

const Archived = styled(Small)`
  width: fit-content;
  padding: ${pad / 4}px ${pad / 2}px;
  border-radius: 10px;
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.warning};
  ${voice.quiet};
`;

const Spaced = styled.div`
  margin-right: ${pad}px;
  height: min-content;
  margin-left: ${pad / 2}px;

  ${bp(2)} {
    margin-left: ${pad}px;
  }
`;

const StyledSearchWrapper = styled(SearchWrapper)`
  max-width: calc(100% - 176px);
`;

const PositionedDiv = styled.div`
  position: relative;
`;

const Archive = styled(Button)`
  padding: ${pad / 2}px ${pad}px;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.warning};

  ${({restore, theme}) =>
    restore &&
    css`
      background: ${theme.success};
    `}
`;

const Delete = styled(Button)`
  padding: ${pad / 2}px ${pad}px;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.error};
`;

const ApplyFilter = styled(Button)`
  ${voice.quiet};
  width: fit-content;
  height: min-content;
  white-space: nowrap;
  padding: ${pad / 2}px;
  margin: ${pad / 2}px 0 0 0;

  > abbr > svg {
    fill: ${({theme}) => theme.tertiary};
    align-self: center;
  }
`;

const OptionSub = styled.div`
  ${flex("row", "nowrap", "start", "start")}
  position: absolute;
  top: 29px;
  right: 0;
  width: 90px;
  gap: ${pad / 2}px;
  border: ${border} solid ${({theme}) => theme.primary};
  border-radius: ${radius};
  padding: ${pad}px;
  background: ${({theme}) => theme.tertiary};
  margin-top: ${pad}px;
  z-index: ${z("top")};
  box-shadow: ${shadow};

  color: ${({theme}) => theme.secondary};

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

export default TableReportSharepoint;
